/**
 * Contratos de compras e entradas (Fase 4). O mesmo schema valida o formulário
 * no frontend e a requisição na API; o banco repete as regras essenciais.
 */
import { z } from "zod";
import { decimalSchema, versionSchema } from "./cadastros";
import { DOC_STATUSES, type DocStatus, type StockMovement } from "./estoque";

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === "" ? null : v))
    .nullable()
    .optional()
    .transform((v) => v ?? null);
const isoDate = z
  .union([z.iso.date("Data inválida"), z.literal(""), z.null()])
  .optional()
  .transform((v) => (v ? v : null));
const requiredDate = z.iso.date("Informe a data");
const optionalUuid = z
  .union([z.uuid(), z.literal(""), z.null()])
  .optional()
  .transform((v) => (v ? v : null));
const positive = (scale = 6) =>
  decimalSchema(scale).refine((v) => Number(v) > 0, "A quantidade precisa ser maior que zero");

/** Maior valor em R$ que o banco guarda (numeric(14,2)). */
const MONEY_MAX = 999_999_999_999;

/** Valor em R$ (até 2 casas, não negativo). Vazio = 0. */
export const moneySchema = z
  .union([decimalSchema(2, { max: MONEY_MAX }), z.literal(""), z.null()])
  .optional()
  .transform((v) => (v === "" || v === null || v === undefined ? "0" : v));
const positiveMoney = decimalSchema(2, { max: MONEY_MAX }).refine(
  (v) => Number(v) > 0,
  "Informe um valor maior que zero",
);

/** Lote do fornecedor: mesmas regras do cadastro de lotes. Vazio/null = sem lote. */
const lotCodeSchema = z
  .union([
    z
      .string()
      .trim()
      .refine(
        (v) => v === "" || /^[A-Za-z0-9][A-Za-z0-9._/-]{0,39}$/.test(v),
        "Lote: até 40 letras, números ou . _ / -",
      ),
    z.null(),
  ])
  .optional()
  .transform((v) => (v ? v : null));

// ---------------------------------------------------------------- nota fiscal
export const INVOICE_MODELS = ["55", "01", "04", "outro"] as const;
export type InvoiceModel = (typeof INVOICE_MODELS)[number];
export const INVOICE_MODEL_LABEL: Record<InvoiceModel, string> = {
  "55": "NF-e (modelo 55)",
  "01": "Nota fiscal modelo 1/1A",
  "04": "Nota de produtor rural",
  outro: "Outro documento",
};
const digitsNoLeadingZeros = (max: number, label: string, allowZero: boolean) =>
  z
    .string()
    .trim()
    .refine(
      (v) => v === "" || new RegExp(`^\\d{1,${max}}$`).test(v),
      `${label}: até ${max} dígitos`,
    )
    .refine((v) => allowZero || v === "" || Number(v) > 0, `${label} não pode ser zero`)
    .transform((v) => (v === "" ? null : String(Number(v))))
    .nullable()
    .optional()
    .transform((v) => v ?? null);

const invoiceFields = {
  invoiceModel: z
    .union([z.enum(INVOICE_MODELS), z.literal(""), z.null()])
    .optional()
    .transform((v) => (v ? v : null)),
  invoiceSeries: digitsNoLeadingZeros(3, "Série", true),
  invoiceNumber: digitsNoLeadingZeros(9, "Número da nota", false),
};
const invoiceRefine = (v: { invoiceModel: string | null; invoiceNumber: string | null }) =>
  v.invoiceNumber === null || v.invoiceModel !== null;
/** NF-e sempre tem série (a mesma nota lançada à mão e por XML precisa ser reconhecida). */
const seriesRefine = (v: { invoiceModel: string | null; invoiceSeries: string | null }) =>
  v.invoiceModel !== "55" || v.invoiceSeries !== null;

export const ENTRY_ORIGIN_LABEL = { manual: "Lançamento manual", xml: "XML da NF-e" } as const;
export type EntryOrigin = keyof typeof ENTRY_ORIGIN_LABEL;

// ---------------------------------------------------------------- entrada: cabeçalho
const headerFields = {
  warehouseId: z.uuid("Selecione o depósito"),
  entryDate: requiredDate,
  issueDate: isoDate,
  paymentMethodId: optionalUuid,
  /** false = não gera contas a pagar (bonificação, remessa, já pago fora do sistema) */
  generatesPayable: z.boolean().default(true),
  notes: optionalText(1000),
};
const datesRefine = (v: { entryDate: string; issueDate: string | null }) =>
  !v.issueDate || v.issueDate <= v.entryDate;

export const purchaseEntryCreateSchema = z
  .object({ supplierId: z.uuid("Selecione o fornecedor"), ...headerFields, ...invoiceFields })
  .refine(invoiceRefine, {
    message: "Informe o modelo do documento junto com o número.",
    path: ["invoiceModel"],
  })
  .refine(datesRefine, {
    message: "A emissão não pode ser depois da entrada.",
    path: ["issueDate"],
  })
  .refine(seriesRefine, { message: "Informe a série da NF-e.", path: ["invoiceSeries"] });
export type PurchaseEntryCreateInput = z.input<typeof purchaseEntryCreateSchema>;

export const entryInstallmentSchema = z.object({
  dueDate: requiredDate,
  amount: positiveMoney,
  document: optionalText(60),
});

/**
 * Dados do rascunho + parcelas previstas. Em entrada de XML, fornecedor e dados da
 * nota vêm do arquivo e não mudam (a API recusa alteração).
 */
export const purchaseEntryUpdateSchema = z
  .object({
    supplierId: z.uuid("Selecione o fornecedor"),
    ...headerFields,
    ...invoiceFields,
    /**
     * Valor total da nota = valor a pagar (vazio = o custo somado dos itens). Pode ser
     * diferente do custo (ex.: ICMS desonerado abatido na nota). Em entrada de XML vem
     * do arquivo e não muda.
     */
    invoiceTotal: z
      .union([decimalSchema(2, { max: MONEY_MAX }), z.literal(""), z.null()])
      .optional()
      .transform((v) => (v ? v : null)),
    installments: z.array(entryInstallmentSchema).max(120, "Máximo de 120 parcelas"),
    ...versionSchema.shape,
  })
  .refine(invoiceRefine, {
    message: "Informe o modelo do documento junto com o número.",
    path: ["invoiceModel"],
  })
  .refine(datesRefine, {
    message: "A emissão não pode ser depois da entrada.",
    path: ["issueDate"],
  })
  .refine(seriesRefine, { message: "Informe a série da NF-e.", path: ["invoiceSeries"] });
export type PurchaseEntryUpdateInput = z.input<typeof purchaseEntryUpdateSchema>;

// ---------------------------------------------------------------- entrada: itens
const lotFields = {
  lotCode: lotCodeSchema,
  lotManufacturedOn: isoDate,
  lotExpiresOn: isoDate,
};
const lotRefine = (v: { lotManufacturedOn: string | null; lotExpiresOn: string | null }) =>
  !v.lotManufacturedOn || !v.lotExpiresOn || v.lotExpiresOn >= v.lotManufacturedOn;

/** Item lançado à mão: produto, quantidade na unidade escolhida e preço unitário. */
export const manualItemSchema = z
  .object({
    productId: z.uuid("Selecione o produto"),
    unitId: z.uuid("Selecione a unidade"),
    quantity: positive(),
    unitPrice: decimalSchema(6),
    discount: moneySchema,
    ipi: moneySchema,
    icmsSt: moneySchema,
    ...lotFields,
    notes: optionalText(300),
  })
  .refine(lotRefine, {
    message: "A validade não pode ser anterior à fabricação.",
    path: ["lotExpiresOn"],
  });
export type ManualItemInput = z.input<typeof manualItemSchema>;

/** Itens da entrada manual (substitui todos). Frete, seguro e outras são rateados pelo valor. */
export const manualItemsSchema = z.object({
  freight: moneySchema,
  insurance: moneySchema,
  other: moneySchema,
  items: z.array(manualItemSchema).max(990, "Máximo de 990 itens"),
  ...versionSchema.shape,
});
export type ManualItemsInput = z.input<typeof manualItemsSchema>;

/** Vínculo de um item do XML com o produto do CaféGestor (de/para). */
export const itemMappingSchema = z
  .object({
    productId: z.uuid("Selecione o produto"),
    unitId: z.uuid("Selecione a unidade"),
    /** quantas unidades do CaféGestor há em 1 unidade do fornecedor */
    conversion: positive(),
    ...lotFields,
    /** grava o de/para para as próximas notas deste fornecedor */
    remember: z.boolean().default(true),
    ...versionSchema.shape,
  })
  .refine(lotRefine, {
    message: "A validade não pode ser anterior à fabricação.",
    path: ["lotExpiresOn"],
  });
export type ItemMappingInput = z.input<typeof itemMappingSchema>;

// ---------------------------------------------------------------- importação e anexos
export const NFE_XML_MAX_BYTES = 5 * 1024 * 1024;
export const ATTACHMENT_MAX_BYTES = 5 * 1024 * 1024;

export const importXmlSchema = z.object({
  xml: z.string().min(1, "Envie o arquivo XML").max(NFE_XML_MAX_BYTES, "XML maior que 5 MB"),
  fileName: z
    .union([z.string().trim().max(200), z.null()])
    .optional()
    .transform((v) => v || null),
  warehouseId: optionalUuid,
});
export type ImportXmlInput = z.input<typeof importXmlSchema>;

export const attachmentUploadSchema = z.object({
  fileName: z.string().trim().min(1, "Informe o nome do arquivo").max(200),
  /** conteúdo em base64 (até 5 MB depois de decodificado) */
  contentBase64: z
    .string()
    .min(4)
    .max(Math.ceil((ATTACHMENT_MAX_BYTES * 4) / 3) + 4, "Arquivo maior que 5 MB")
    .regex(/^[A-Za-z0-9+/]+={0,2}$/, "Conteúdo inválido"),
  description: optionalText(200),
});
export type AttachmentUploadInput = z.input<typeof attachmentUploadSchema>;

// ---------------------------------------------------------------- consultas
export const entryQuerySchema = z.object({
  status: z.enum([...DOC_STATUSES, "todos"]).default("todos"),
  q: z.string().trim().max(100).optional(),
  supplierId: z.uuid().optional(),
  from: z.iso.date().optional(),
  to: z.iso.date().optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});

export const linkQuerySchema = z.object({
  supplierId: z.uuid().optional(),
  productId: z.uuid().optional(),
  q: z.string().trim().max(100).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});

export const linkUpdateSchema = z.object({
  productId: z.uuid("Selecione o produto"),
  unitId: z.uuid("Selecione a unidade"),
  conversion: positive(),
  description: optionalText(200),
  ...versionSchema.shape,
});
export type LinkUpdateInput = z.input<typeof linkUpdateSchema>;

// ---------------------------------------------------------------- respostas
export type PurchaseEntryItem = {
  id: string;
  lineNumber: number;
  sourceLine: number | null;
  supplierCode: string | null;
  supplierDescription: string | null;
  supplierUnit: string | null;
  supplierQuantity: string | null;
  supplierUnitPrice: string | null;
  supplierGtin: string | null;
  supplierLot: string | null;
  ncm: string | null;
  cfop: string | null;
  productId: string | null;
  productCode: string | null;
  productName: string | null;
  controlsLot: boolean;
  unitId: string | null;
  unitCode: string | null;
  baseUnitCode: string | null;
  conversion: string;
  quantity: string | null;
  lotCode: string | null;
  lotManufacturedOn: string | null;
  lotExpiresOn: string | null;
  lotId: string | null;
  productsValue: string;
  discount: string;
  freight: string;
  insurance: string;
  other: string;
  ipi: string;
  icmsSt: string;
  totalCost: string;
  unitCost: string | null;
  notes: string | null;
};

export type EntryInstallment = {
  id: string;
  number: number;
  dueDate: string;
  amount: string;
  document: string | null;
};

export type EntryFile = {
  id: string;
  fileId: string;
  kind: "nfe_xml" | "anexo";
  fileName: string;
  contentType: string;
  sizeBytes: number;
  sha256: string;
  description: string | null;
  createdAt: string;
  createdByEmail: string | null;
};

export type PurchaseEntry = {
  id: string;
  number: string;
  status: DocStatus;
  origin: EntryOrigin;
  supplierId: string;
  supplierName: string;
  supplierDocument: string | null;
  warehouseId: string;
  warehouseName: string;
  entryDate: string;
  issueDate: string | null;
  invoiceModel: InvoiceModel | null;
  invoiceSeries: string | null;
  invoiceNumber: string | null;
  accessKey: string | null;
  paymentMethodId: string | null;
  paymentMethodName: string | null;
  generatesPayable: boolean;
  productsTotal: string;
  discountTotal: string;
  freightTotal: string;
  insuranceTotal: string;
  otherTotal: string;
  ipiTotal: string;
  icmsStTotal: string;
  totalAmount: string;
  invoiceTotal: string | null;
  /** valor do título a pagar: total da nota (vNF) ou, sem ele, o custo somado dos itens */
  payableAmount: string;
  notes: string | null;
  itemCount: number;
  pendingItems: number;
  createdAt: string;
  createdByEmail: string | null;
  confirmedAt: string | null;
  reversedAt: string | null;
  reversalReason: string | null;
  version: number;
  payableId: string | null;
  payableNumber: string | null;
  payableStatus: string | null;
  items?: PurchaseEntryItem[];
  installments?: EntryInstallment[];
  files?: EntryFile[];
  movements?: StockMovement[];
  /** o que falta para confirmar (vazio = pronta) */
  issues?: string[];
  /** avisos da importação (não impedem a confirmação) */
  warnings?: string[];
};

export type SupplierProductLink = {
  id: string;
  supplierId: string;
  supplierName: string;
  supplierCode: string;
  supplierUnit: string;
  productId: string;
  productCode: string;
  productName: string;
  unitId: string;
  unitCode: string;
  conversion: string;
  description: string | null;
  lastUsedAt: string | null;
  version: number;
};

/**
 * CFOP de remessa/retorno/outras (x.9xx): a nota não é uma compra. A entrada é
 * criada sem contas a pagar e com aviso para conferir se a mercadoria é da empresa.
 */
export function isNonPurchaseCfop(cfop: string | null): boolean {
  return cfop !== null && /^[1-7]9\d\d$/.test(cfop);
}

/**
 * CFOP de mercadoria de TERCEIROS que não entra no estoque próprio: industrialização
 * por encomenda, demonstração, consignação e conserto. A nota não é importada.
 */
export function isThirdPartyGoodsCfop(cfop: string | null): boolean {
  return cfop !== null && /^[1-7]9(01|12|17|15)$/.test(cfop);
}
